import React from 'react';
import { NavLink } from 'react-router-dom';
import './SectionTabs.css';

/**
 * SectionTabs — the single in-page tab strip used across the app.
 *
 * Two modes, one look:
 *
 *   Routed (default) — each tab is a URL.
 *     <SectionTabs ariaLabel="People sections"
 *                  tabs={[{ to: '/people', label: 'People', Icon: Users, end: true }]} />
 *
 *   Controlled — the strip drives local state instead of the URL.
 *     <SectionTabs value={tab} onChange={setTab}
 *                  tabs={[{ id: 'mine', label: 'My Communities', count: 3 }]} />
 *
 * The two modes deliberately carry different ARIA. Routed tabs are navigation:
 * a <nav> of links, with NavLink's own aria-current="page" marking the active
 * one. Controlled tabs really are a tablist, so they get role="tablist" /
 * role="tab" / aria-selected. Putting role="tab" on a link would promise a
 * tabpanel and roving tabindex that routed navigation does not have.
 *
 * Per-tab fields: `label` (required), `Icon`, `count`, `highlight` (renders the
 * count in the attention colour), and `end` for routed tabs whose path is a
 * prefix of a sibling's.
 *
 * Three tabs is the ceiling. The strip never scrolls horizontally, so a fourth
 * tab squeezes the other three rather than hiding — see SectionTabs.css.
 */
export default function SectionTabs({ tabs, value, onChange, className = '', ariaLabel }) {
  const list = (tabs || []).filter(Boolean);
  if (list.length === 0) return null;

  const controlled = typeof onChange === 'function';

  function inner({ Icon, label, count, highlight }, isActive) {
    return (
      <>
        {Icon && <Icon className="st-tab-icon" size={16} strokeWidth={isActive ? 2.5 : 2} />}
        <span className="st-tab-label">{label}</span>
        {count > 0 && (
          <span className={`st-tab-count${highlight ? ' highlight' : ''}`}>{count}</span>
        )}
      </>
    );
  }

  if (controlled) {
    return (
      <div className={`st-tabs ${className}`} role="tablist" aria-label={ariaLabel}>
        {list.map((tab) => {
          const isActive = value === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`st-tab${isActive ? ' active' : ''}`}
              onClick={() => onChange(tab.id)}>
              {inner(tab, isActive)}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <nav className={`st-tabs ${className}`} aria-label={ariaLabel}>
      {list.map((tab) => (
        <NavLink
          key={tab.to || tab.label}
          to={tab.to}
          end={tab.end}
          replace
          className={({ isActive }) => `st-tab${isActive ? ' active' : ''}`}>
          {({ isActive }) => inner(tab, isActive)}
        </NavLink>
      ))}
    </nav>
  );
}
